/**
 * Physicians - CRUD component for managing physicians.
 *
 * Features:
 * - Server-side pagination with URL state sync
 * - Real-time updates via GraphQL subscriptions
 * - Schedule access management
 * - Procedure types association
 * - CSV upload support
 */

import React, { Fragment } from 'react';
import { useQuery } from '@apollo/client';
import get from 'lodash/fp/get';
import { set } from 'lodash/fp';
import GraphQLCRUD from '@/se/components/GraphQLCRUD';
import EntityView from '@/se/components/entity/EntityView';

import PhysicianProcedureTypesForm from './PhysicianProcedureTypesForm';
import PhysicianMobileListItem from './PhysicianMobileListItem';
import PhysicianFilterBar from './PhysicianList/PhysicianFilterBar';
import PhysicianInput from '../../inputs/physician/PhysicianInput';
import { PhysicianUploadButton } from '../../inputs/upload/UploadButton';

import ScheduleAccessEditor from '../../pages/ScheduleAccessEditor';
import ScheduleAccessToggle from '../../pages/ScheduleAccessToggle';

import scheme, {
  grantScheduleAccess,
  resendScheduleAccessLink,
  revokeScheduleAccess,
  setScheduleAccessAllPhysician,
  physiciansPaginatedQuery,
  physiciansCountQuery,
  subscription,
} from '../../../graph/physicians';

import { listColumns, viewColumns } from './columns';

import type { RouteComponentProps } from 'react-router-dom';
import type Physician from '@/types/Physician';

// =============================================================================
// Types
// =============================================================================

interface PhysicianScheduleAccessEditorProps {
  physician?: Physician;
  onClose?: () => void;
  refetchQueries?: Array<{ query: unknown; variables?: unknown }>;
}

interface PhysicianAndProcedureTypesViewProps {
  [key: string]: unknown;
}

interface ScheduleAccessRouteProps extends RouteComponentProps<{ id: string }> {}

// =============================================================================
// Schedule Access Editor Component
// =============================================================================

/**
 * Component for managing physician schedule access permissions.
 * Allows granting, revoking, and resending schedule access links.
 */
export const PhysicianScheduleAccessEditor: React.FC<PhysicianScheduleAccessEditorProps> = ({
  physician,
  onClose,
  refetchQueries = [],
  ...rest
}) => (
  <ScheduleAccessEditor
    grantScheduleAccess={grantScheduleAccess}
    resendScheduleAccessLink={resendScheduleAccessLink}
    revokeScheduleAccess={revokeScheduleAccess}
    {...physician}
    onClose={onClose}
    {...rest}
  >
    <ScheduleAccessToggle
      scheduleUserId={physician?.id}
      scheduleAccessAll={physician?.scheduleAccessAll}
      setScheduleAccessAll={setScheduleAccessAllPhysician}
      refetchQueries={refetchQueries}
    />
  </ScheduleAccessEditor>
);

PhysicianScheduleAccessEditor.displayName = 'PhysicianScheduleAccessEditor';

// =============================================================================
// Detail View Component
// =============================================================================

/**
 * Combined view showing physician details and procedure types form.
 */
const PhysicianAndProcedureTypesView: React.FC<PhysicianAndProcedureTypesViewProps> = props => (
  <Fragment>
    <EntityView {...props} />
    <PhysicianProcedureTypesForm {...props} scheme={scheme} />
  </Fragment>
);

PhysicianAndProcedureTypesView.displayName = 'PhysicianAndProcedureTypesView';

// =============================================================================
// Schedule Access Route Component
// =============================================================================

/**
 * Route component for the schedule access editor.
 * Fetches physician data and renders the editor.
 */
const ScheduleAccessRoute: React.FC<ScheduleAccessRouteProps> = props => {
  const physicianId = parseInt(get('params.id')(props.match), 10);

  const { data: item, loading } = useQuery(scheme.item, {
    variables: { id: physicianId },
    skip: isNaN(physicianId),
  });

  if (loading || !item?.physician) {
    return null;
  }

  return <PhysicianScheduleAccessEditor {...item} {...props} />;
};

ScheduleAccessRoute.displayName = 'ScheduleAccessRoute';

// =============================================================================
// Main CRUD Export
// =============================================================================

/**
 * Physicians CRUD component using GraphQLCRUD HOC.
 *
 * Configuration:
 * - List: Paginated table with server-side filtering and sorting
 * - Show: Detail view with procedure types form
 * - Create: Form for creating new physicians
 * - Edit: Form for editing existing physicians
 */
export default GraphQLCRUD({
  entityName: 'Physician',

  List: {
    tableKey: 'Physicians',
    columns: listColumns,
    Actions: PhysicianUploadButton,
    MobileItemComponent: PhysicianMobileListItem,
    simpleFilter: true,
    FilterComponent: PhysicianFilterBar,

    // Server-side pagination configuration
    paginationConfig: {
      query: physiciansPaginatedQuery,
      countQuery: physiciansCountQuery,
      subscription: subscription,
      entityKey: 'physicians',
      countKey: 'physiciansCount',
      // pageSize defaults to 50 via DEFAULT_PAGE_SIZE
    },

    // Additional routes under /physicians/*
    additionalRoutes: [
      {
        key: 'schedule-access',
        path: '/schedule-access/:id',
        Component: ScheduleAccessRoute,
      },
    ],
  },

  Show: {
    columns: viewColumns,
    View: PhysicianAndProcedureTypesView,
  },

  Create: {
    Input: PhysicianInput,
  },

  Edit: {
    Input: PhysicianInput,
    // Map bracelet.id to braceletId for the form
    mapEditItemProps: (props: { data?: { physician?: { bracelet?: { id?: number } } } }) =>
      set('data.physician.braceletId', get('data.physician.bracelet.id')(props))(props),
  },

  scheme,
});
